import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import { ACL_TYPE } from '@openx/types/aclType';
import type { DealFormData } from '@openx/types/deal';
import type { ThirdPartyPartnerOptionApi } from '@openx/types/options';
import { microToPercentWithPrecision } from '@openx/utils/lib/microToPercent';
import { currencySymbol } from '@openx/utils/lib/priceFormat';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { type SyntheticEvent, useCallback, useEffect } from 'react';
import { type Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDefaultPlatformShare } from '../hooks/useDefaultPlatformShare';
import { FeeDescription } from './settings/FeeDescription';
import { GrossFee } from './settings/GrossFee';
import { NetFee } from './settings/NetFee';
import { OpenXPlatformFee } from './settings/OpenXPlatformFee';
import { RevenueMethod } from './settings/RevenueMethod';

type ThirdPartyFeeConfigProps = {
  index: number;
  isFilledOut: boolean;
  isLoading: boolean;
  partnerById: Record<string, ThirdPartyPartnerOptionApi>;
  partnerIdList: string[];
};

export const ThirdPartyFeeConfig = ({
  index,
  isFilledOut,
  isLoading,
  partnerById,
  partnerIdList,
}: ThirdPartyFeeConfigProps) => {
  const grossCpmCapFieldName: Path<DealFormData> = `third_party_fees_config.${index}.gross_cpm_cap`;
  const grossShareFieldName: Path<DealFormData> = `third_party_fees_config.${index}.gross_share`;
  const partnerIdFieldName: Path<DealFormData> = `third_party_fees_config.${index}.partner_id`;

  const observableCurrency = useWatch<DealFormData, 'currency'>({ name: 'currency' });
  const observablePartnerId = useWatch<DealFormData, typeof partnerIdFieldName>({ name: partnerIdFieldName });

  const { getValues, resetField, setValue } = useFormContext<DealFormData>();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const { defaultPlatformShare } = useDefaultPlatformShare(index, partnerById);
  const { t } = useTranslation();

  const currency = currencySymbol(observableCurrency ?? 'USD');
  const hasSinglePartner = partnerIdList.length === 1;
  const partner = observablePartnerId ? partnerById[observablePartnerId] : null;

  const hasReadPermission = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_READ));
  const hasWritePermission = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_WRITE));

  const handlePartnerChange = useCallback(
    (_: SyntheticEvent, value: string | null) => {
      resetField(`third_party_fees_config.${index}`);
      setValue(partnerIdFieldName, value);
    },
    [index, partnerIdFieldName, resetField, setValue]
  );

  // autofill the `gross_cpm_cap` when the `default_cpm_cap` is configured for selected Partner
  useEffect(() => {
    const hasGrossCpmCapFilledOut = !!getValues(grossCpmCapFieldName);

    if (partner?.default_cpm_cap && !hasGrossCpmCapFilledOut) {
      setValue(grossCpmCapFieldName, partner.default_cpm_cap, { shouldValidate: true });
    }
  }, [partner?.default_cpm_cap, getValues, grossCpmCapFieldName, setValue]);

  // autofill the `gross_share` when the `default_share` is configured for selected Partner
  useEffect(() => {
    const hasGrossShareFilledOut = !!getValues(grossShareFieldName);

    if (partner?.default_share && !hasGrossShareFilledOut) {
      setValue(grossShareFieldName, microToPercentWithPrecision(partner.default_share) ?? '', { shouldValidate: true });
    }
  }, [partner?.default_share, getValues, grossShareFieldName, setValue]);

  return (
    <Stack>
      <RHFAutoComplete
        data-test="third-party-partner"
        disableClearable
        disabled={hasSinglePartner || isFilledOut}
        fullWidth
        getOptionLabel={option => partnerById[option]?.name ?? option}
        loading={isLoading}
        name={partnerIdFieldName}
        onChange={handlePartnerChange}
        options={partnerIdList}
        textFieldProps={{
          label: t('Partner'),
          margin: 'dense',
          placeholder: t('[ Select Partner ]'),
        }}
      />

      {observablePartnerId && (
        <>
          {/* changing the revenue method of an existing fee is treated by the API as removing the fee, which requires the write ACL */}
          <RevenueMethod index={index} isDisabled={isFilledOut && !hasWritePermission} />

          <IndentedSections>
            <Grid container spacing={1}>
              <Grid size="grow" sx={{ paddingRight: hasReadPermission ? 4 : 0 }}>
                <GrossFee currency={currency} index={index} />
              </Grid>

              {hasReadPermission && (
                <Grid size="grow">
                  <NetFee currency={currency} defaultPlatformShare={defaultPlatformShare} index={index} />
                </Grid>
              )}
            </Grid>

            <FeeDescription defaultFeesDescription={partner?.default_fees_description} />
          </IndentedSections>

          {hasWritePermission && <OpenXPlatformFee defaultPlatformShare={defaultPlatformShare} index={index} />}
        </>
      )}
    </Stack>
  );
};
